import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import type { AppState } from '@zerops/zerops/app';
import { ZefForm, ZefFormsManagerService } from '@zerops/zef/forms';
import { box, Boxed, FormGroupState, FormState, setErrors, validate } from 'ngrx-forms';
import { required } from 'ngrx-forms/validation';
import { FEATURE_NAME } from './token-add-edit-dialog.constant';
import { IntegrationTokenProjectAccess, IntegrationTokenRoleCode } from '@zerops/models/integration-token';

export interface TokenAddEditFormValue {
  name: string;
  mode: 'user' | 'integration';
  roleCode?: IntegrationTokenRoleCode;
  canCreateProjects?: boolean;
  projects: Boxed<IntegrationTokenProjectAccess[]>;
}

export type TokenAddEditFormState = FormState<TokenAddEditFormValue>;

/**
 * Validates token projects field based on mode, role, and canCreateProjects
 *
 * Business Rules:
 * - USER mode: No validation (user tokens don't have project restrictions)
 * - INTEGRATION + NO_ACCESS: Must have at least one project, UNLESS canCreateProjects=true
 *   (a NO_ACCESS token with no projects but canCreateProjects=true is meaningful — it can
 *   create new projects and own them, even though it has no rights on existing ones)
 * - INTEGRATION + ADMIN/READ_ONLY: Projects optional (empty = all projects, non-empty = restricted)
 */
function validateTokenProjects(
  mode: string | undefined,
  roleCode: string | undefined,
  canCreateProjects: boolean | undefined,
  projects: IntegrationTokenProjectAccess[]
): { required: { actual: any } } | null {
  if (mode !== 'integration') {
    return null;
  }

  if (roleCode === IntegrationTokenRoleCode.NoAccess) {
    if (projects.length === 0 && !canCreateProjects) {
      return { required: { actual: projects } };
    }
  }

  return null;
}

@Injectable({ providedIn: 'root' })
export class TokenAddEditForm extends ZefForm<TokenAddEditFormValue> {
  constructor(
    public store: Store<AppState>,
    public formsManager: ZefFormsManagerService
  ) {
    super(
      store,
      formsManager,
      FEATURE_NAME,
      {
        name: '',
        mode: 'user',
        roleCode: IntegrationTokenRoleCode.Admin,
        canCreateProjects: false,
        projects: box([])
      },
      {
        name: validate(required),

        roleCode: (s, ps: FormGroupState<TokenAddEditFormValue>) => {
          if (ps?.controls?.mode.value === 'integration') {
            return validate(s, required);
          }
          return setErrors(s, {});
        },

        canCreateProjects: (s, ps: FormGroupState<TokenAddEditFormValue>) => {
          // Only relevant for NO_ACCESS role
          if (ps?.controls?.mode.value === 'integration' &&
              ps?.controls?.roleCode.value === IntegrationTokenRoleCode.NoAccess) {
            return s;
          }
          return s;
        },

        projects: (s, ps: FormGroupState<TokenAddEditFormValue>) => {
          const errors = validateTokenProjects(
            ps?.controls?.mode.value,
            ps?.controls?.roleCode.value,
            ps?.controls?.canCreateProjects.value,
            s.value?.value || []
          );
          return setErrors(s, errors || {});
        }
      }
    );
  }
}
